import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "./cn";

/**
 * Bun toasts. Spec: bottom-center, auto-dismiss 4s, ONE at a time. Ink chip,
 * 13/550 text, colored icon, optional action ("Undo").
 *
 * Wrap a Bun screen (or the app shell) in <ToastProvider>, then:
 *   const toast = useToast();
 *   toast.success("Transaction marked reviewed", { action: { label: "Undo", onClick } });
 *   toast.warning("Couldn't sync, retrying");
 *   toast.info("Saved");
 */
const ToastContext = createContext(null);

const ICONS = {
  success: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="text-bun-toast-success shrink-0">
      <path d="M5 13l4 4L19 7" />
    </svg>
  ),
  warning: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-bun-toast-warning shrink-0">
      <path d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
    </svg>
  ),
  info: null,
};

const AUTO_DISMISS_MS = 4000;

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    clearTimeout(timerRef.current);
    setToast(null);
  }, []);

  const show = useCallback((kind, message, options = {}) => {
    clearTimeout(timerRef.current);
    setToast({ kind, message, action: options.action, id: Date.now() });
    timerRef.current = setTimeout(() => setToast(null), options.duration ?? AUTO_DISMISS_MS);
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const api = useMemo(
    () => ({
      success: (message, options) => show("success", message, options),
      warning: (message, options) => show("warning", message, options),
      info: (message, options) => show("info", message, options),
      dismiss,
    }),
    [show, dismiss]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toast &&
        createPortal(
          <div className="fixed bottom-6 inset-x-0 z-[60] flex justify-center pointer-events-none px-4">
            <div
              role="status"
              className={cn(
                "pointer-events-auto flex items-center gap-[10px] max-w-full",
                "bg-bun-ink text-bun-on-ink rounded-bun-lg px-4 py-3 shadow-bun-toast",
                "font-instrument text-bun-body font-strong"
              )}
            >
              {ICONS[toast.kind]}
              <span className="min-w-0 truncate">{toast.message}</span>
              {toast.action && (
                <button
                  type="button"
                  onClick={() => {
                    toast.action.onClick?.();
                    dismiss();
                  }}
                  className="bg-transparent border-0 p-0 cursor-pointer font-instrument text-bun-body font-strong text-bun-on-ink-muted hover:text-bun-on-ink"
                >
                  {toast.action.label}
                </button>
              )}
            </div>
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside a <ToastProvider>");
  }
  return ctx;
}
